/**
 * Returns the appropriate symbol for a given currency name.
 *
 * @param {"Dollars"|"Colones"|string} currency - The currency label.
 * @returns {string} The corresponding symbol or fallback text.
 *
 * @description
 * Provides symbol consistency across components.
 * If the currency is not recognized, returns the input string unchanged.
 */
export const currencySymbolFormatter = (currency) => {
    switch (currency) {
        case 'Dollars':
            return "$"
        case 'Colones':
            return '₡'
        default:
            return currency
    }
}

/**
 * Formats an amount with its currency symbol and locale.
 *
 * @param {"Dollars"|"Colones"|string} currency - Currency type.
 * @param {number} amount - The numeric amount to format.
 * @returns {string} Formatted currency string or "Not provided".
 *
 * @description
 * Uses `Intl.NumberFormat` for locale-aware monetary formatting.  
 * If the amount is missing or invalid, returns a fallback message.
 */
export const currencyAmountFormatter = (currency, amount) => {
    if (amount) {
        switch (currency) {
            case 'Dollars':
                return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)
            case 'Colones':
                return new Intl.NumberFormat('es-CR', { style: 'currency', currency: 'CRC' }).format(amount)
            default:
                return currency
        }
    }
    return "Not provided"
}


/**
 * Formats numeric values as general quantities, percentages, or units.
 *
 * @param {number} amount - The numeric value to format.
 * @param {"decimal"|"percent"|"unit"|undefined} [style] - Intl format style.
 * @param {string|undefined} [unit] - The unit to use (when style="unit").
 * @returns {string} Formatted numeric string or "Not provided".
 *
 * @description
 * General-purpose number formatter for various contexts (percentages, units, etc.).
 * Ensures a consistent look across charts and summaries.
 */
export const amountFormatter = (amount, style = undefined, unit = undefined) => {
    if (amount) {
        return new Intl.NumberFormat("en-US", {
            style,
            unit,
            minimumFractionDigits: style === "percent" ? 2 : undefined,
        }).format(amount);
    }
    return "Not provided"
}